'use client';

import { getPhaserAttributesFromUrl, phaserAttributesSet } from '@modules/phaser';
import { InteractionHeader, type InteractionHeaderButtonProps, type InteractionHeaderProps } from '@tuic/web-compositions/my-trips/InteractionHeader';
import { useEffect, useMemo, useState } from 'react';
import { HistoryAwareBackLink } from '../../../../../app/[locale]/[bookingCode]/_components/history-aware-back-link';
import { urls } from '@lib/urls';
import { useClientTranslation } from '@lib/i18n/client';
import { useThirdPartyEvent } from '@tuic/lib/src/hooks';
import { EVENT_PHASER_OPENER } from '@modules/phaser';
import { logger } from '@tuic/logger';
import { telemetryToClickAttributes } from '@modules/prism-client/lib/telemetry';
import { Mxp365Link } from '../../mxp365/Mxp365Link';
import type { ReactElement } from 'react';
import { Locale } from '@tuic/i18n';
import { ButtonElementProps } from '../../content-transformers/button';
import { ComponentBlock } from '@modules/prism-client/types/types';
export type InteractionHeaderBlockData = Omit<InteractionHeaderProps, 'leftButton' | 'rightButtons' | 'phaserButton'> & {
  locale: Locale;

  /** Left side button (typically back button) */
  leftButton?: ButtonElementProps;

  /** Right side buttons (home, activities, favorites, etc.) */
  rightButtons?: ButtonElementProps[];

  /** Phaser button configuration */
  shouldRenderPhaser?: boolean;

  /** Current path of the page */
  currentPath: string;
};

/**
 * InteractionHeaderBlock - Business logic wrapper for InteractionHeader UI component
 *
 * Responsibilities:
 * - Transform ButtonElementProps into InteractionHeaderButton with React element children
 * - Handle different link types (regular links, action links like history-back/logout)
 * - Provide a default back button when no leftButton specified
 * - Manage phaser state and integration
 */
export const InteractionHeaderBlock: ComponentBlock<InteractionHeaderBlockData> = ({
  data,
  fallback
}) => {
  const {
    t
  } = useClientTranslation();
  const {
    invoke: openPhaser
  } = useThirdPartyEvent(EVENT_PHASER_OPENER);
  const [isPhased, setIsPhased] = useState<boolean>(false);

  // Track phaser state from URL
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const attrs = getPhaserAttributesFromUrl(window.location.href);
    setIsPhased(phaserAttributesSet(attrs));
  }, []);

  // Phaser button configuration
  const phaserButton = useMemo(() => {
    if (!data?.shouldRenderPhaser) {
      return undefined;
    }
    return {
      children: <button onClick={() => queueMicrotask(() => openPhaser())} />,
      isActive: isPhased,
      label: 'Phaser'
    };
  }, [data?.shouldRenderPhaser, isPhased, openPhaser]);
  if (fallback) {
    return <InteractionHeader fallback />;
  }
  if (!data) {
    return null;
  }

  // Transform left button - default to history-back button if not provided
  const leftButton: InteractionHeaderButtonProps | undefined = data.leftButton ? transformButtonToInteractionHeaderButton(data.leftButton, data) ?? undefined : {
    iconName: 'arrow-left',
    variant: 'secondary',
    label: t('Zurück'),
    children: <HistoryAwareBackLink tryHistoryBack={true} href={urls.index({
      locale: data.locale
    })} />
  };

  // Transform right buttons
  const rightButtons: InteractionHeaderButtonProps[] | undefined = data.rightButtons?.map(button => transformButtonToInteractionHeaderButton(button, data)).filter((button): button is InteractionHeaderButtonProps => button !== null);
  return <InteractionHeader title={data.title} leftButton={leftButton} rightButtons={rightButtons} phaserButton={phaserButton} data-sentry-element="InteractionHeader" data-sentry-component="InteractionHeaderBlock" data-sentry-source-file="InteractionHeaderBlock.tsx" />;
};

/**
 * Transform ButtonElementProps to InteractionHeaderButton
 * Creates appropriate React element children based on link type
 */
const transformButtonToInteractionHeaderButton = (button: ButtonElementProps, data: InteractionHeaderBlockData): InteractionHeaderButtonProps | null => {
  // Determine icon name from iconLeft or iconRight
  const iconName = button.iconLeft ?? button.iconRight;
  if (!iconName) {
    logger.warn('Button has no iconName (iconLeft or iconRight)', button);
    return null;
  }

  // Create a children element based on a link type
  const children = createButtonChildren(button, data);
  if (!children) {
    logger.warn('Could not create button children for button', button);
    return null;
  }
  return {
    iconName,
    variant: button.variant,
    on: button.on,
    label: button.label,
    children
  };
};

/**
 * Create React element children for button based on link type
 * - Regular links → HistoryAwareBackLink
 * - Action: history-back → HistoryAwareBackLink with tryHistoryBack
 * - Action: logout → button with logout handler
 * - Other actions → button with generic action handler
 */
const createButtonChildren = (button: ButtonElementProps, data: InteractionHeaderBlockData): ReactElement | null => {
  const link = button.link;
  const trackingAttributes = telemetryToClickAttributes(button.telemetry);

  // No link - create a disabled/placeholder button
  if (!link) {
    return <button disabled {...trackingAttributes} />;
  }

  // Handle action links
  if (link.type === 'action') {
    switch (link.action) {
      case 'history-back':
        {
          return <HistoryAwareBackLink tryHistoryBack={true} href={urls.index({
            locale: data.locale
          })} {...trackingAttributes} />;
        }
      case 'go-up':
        {
          return <HistoryAwareBackLink href={data.currentPath.split('/').slice(0, -1).join('/') || '/'} tryHistoryBack={false} {...trackingAttributes} />;
        }
      case 'logout':
        {
          return (
            // because content is provided by InteractionHeaderButton:
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            <a href={urls.signOutWithBookingCode({
              locale: data.locale
            })} {...trackingAttributes} />
          );
        }
      default:
        {
          logger.warn(`Unknown action type: ${link.action}`);
          return <button onClick={() => {
            logger.warn(`Action handler not implemented: ${link.action}`);
          }} {...trackingAttributes} />;
        }
    }
  }
  if (link.type === 'mxp365') {
    return <Mxp365Link data={link} {...trackingAttributes} />;
  }

  // Handle regular links
  if (link.type === 'link') {
    return <HistoryAwareBackLink href={link.href ?? '#'} tryHistoryBack={false} {...trackingAttributes} />;
  }

  // Fallback for unknown link types
  logger.warn('Unknown link type', link);
  return <button disabled {...trackingAttributes} data-sentry-component="createButtonChildren" data-sentry-source-file="InteractionHeaderBlock.tsx" />;
};